import React from 'react';

import OEToolbox from '../tools/oe-toolbox-core';

// copies static class fields
export function copyComponentKeys(dst, src)    {
    Object.keys(src).forEach(key => {
        dst[key] = src[key];
    });
    return dst;
}

// translates props with a given function constructState: (props) => props
export function translateProps(constructState) {
    return (WrappedComponent) => {
        return copyComponentKeys(class extends React.PureComponent {

            constructor(props) {
                super(props);
                this.state = constructState({...this.props});
            }

            componentWillReceiveProps(nextProps) {
                this.setState(constructState({...nextProps}));
            }

            render() {
                return <WrappedComponent {...this.state}/>;
            }

        }, WrappedComponent);
    };
}

// wraps a component with an isOpen prop with an isOpen state and managing logic
export function withIsOpenState(WrappedComponent, onOpenStateChanged, onRefChanged) {
    let ret = copyComponentKeys(class extends React.PureComponent {

        constructor(props) {
            super(props);
            
            this.isOpen_ = false;
            this.state = {
                isOpen: this.isOpen_
            };

            this.setOpen = this.setOpen.bind(this);
            this.open = this.open.bind(this);
            this.close = this.close.bind(this);
            this.isOpen = this.isOpen.bind(this);

            this.openState = {setOpen: this.setOpen, open: this.open, close: this.close, isOpen: this.isOpen};

            this.onRef = this.onRef.bind(this);

            this.onToggle = this.onToggle.bind(this);
        }
    
        setOpen(open = true)    {
            if(typeof(this.props.isOpen) === 'boolean' || this.isOpen_ === open) return;
            this.isOpen_ =  open;
            this.setState({isOpen: this.isOpen_});
            this.onOpenStateChanged();
        }
    
        open()  {
            this.setOpen(true);
        }

        close() {
            this.setOpen(false);
        }

        isOpen()    {
            return this.isOpen_;
        }

        toggle()    {
            this.setOpen(!this.isOpen());
        }

        onOpenStateChanged()    {
            if(typeof(onOpenStateChanged) === 'function')  onOpenStateChanged(this, this.isOpen_);
            if(typeof(this.props.onOpenStateChanged) === 'function')  this.props.onOpenStateChanged(this, this.isOpen_);
        }

        isPresentable() {
            return this.ref ? (typeof(this.ref.isPresentable) === 'function' ? this.ref.isPresentable() : true) : false;
        }

        onRef(ref) {
            if(this.ref === ref)    return;
            this.ref = ref;
            if(typeof(onRefChanged) === 'function')  onRefChanged(this, ref);
        }
    
        render()    {
            const isOpen = typeof(this.props.isOpen) === 'boolean' ? this.props.isOpen : this.state.isOpen;
    
            return (
                <WrappedComponent ref={this.onRef} {...this.props} isOpen={isOpen} onToggle={this.onToggle} openState={this.openState}/>
            );
        }
    
        onToggle()    {
            if(typeof(this.props.onToggle) === 'function')   {
                this.props.onToggle();
            }
    
            this.setOpen(!this.state.isOpen);
        }

    }, WrappedComponent);

    ret.defaultProps = OEToolbox.filterObject(ret.defaultProps, null, ['isOpen']);

    return ret;
}

// passthroughs isOpen API to some wrapped component via fref prop 
export function delegateOpenState(WrappedComponent) {
    return copyComponentKeys(class extends React.PureComponent {

        constructor(props) {
            super(props);
            this.onRef = this.onRef.bind(this);
        }
    
        setOpen(open = true, options)   {
            if(this.ref && typeof(this.ref.setOpen) === 'function')  {
                this.ref.setOpen(open, options);
            } else {
                this.open_ = {open: open, options: options};
            }
        }
    
        open(options) {
            this.setOpen(true, options);
        }

        close() {
            this.setOpen(false);
        }

        isOpen()    {
            if(!this.ref) return false;
            if(typeof(this.ref.isOpen) === 'function')  {
                return this.ref.isOpen();
            } else if(OEToolbox.isClassComponent(WrappedComponent)) {
                return this.ref.state.isOpen;
            } else {
                return false;
            }
        }

        toggle()    {
            if(this.ref.toggle) {
                this.ref.toggle();
            } else {
                this.setOpen(!this.isOpen());
            }
        }
    
        onRef(ref) {
            if(this.props.fref) this.props.fref(ref);
            if(this.ref === ref) return;
            this.ref = ref;
            if(!ref || !this.open_) return;
            ref.setOpen(this.open_.open, this.open_.options);
            this.open_ = null;
        }
    
        render() {
            return <WrappedComponent {...this.props} fref={this.onRef}/>;
        }

    }, WrappedComponent);
}

// applies ref prop to fref to start ref forwarding via fref prop
export function frefFromRef(WrappedComponent) {
    return copyComponentKeys(React.forwardRef((props, ref) => (
        <WrappedComponent fref={ref} {...props}/>
    )), WrappedComponent);
}

// applies fref prop to ref to close ref forwarding via fref prop
export function frefToRef(WrappedComponent) {
    return copyComponentKeys(class extends React.PureComponent {

        render() {
            const {fref, ...rest} = this.props;
            return <WrappedComponent {...rest} ref={fref}/>;
        }

    }, WrappedComponent);
}